import { useState } from "react";
import { GridColumn, GridRow, Icon, Popup } from "semantic-ui-react";

import { Editable, useCan } from "components";
import { useIsOverflowing, useTyrePositionName } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES, TYRE_LOCATIONS } from "models";
import { useQuestionResultRemarkMutation } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/QuestionResult/hooks";
import { QuestionResultMedia } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/QuestionResultMedia";
import { TyreActions } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/TyreQuestionResults/components/TyreTable/components/TyreTableRow/components";
import "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/TyreQuestionResults/components/TyreTable/components/TyreTableRow/TyreTableRow.scss";
import {
  getFullTyreDescription,
  getTyreSize
} from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/TyreQuestionResults/utils";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { getDBBComment } from "modules/CarDetails/components/Tyres/components/DBBLabelsModal/utils";
import { isAppointmentCanceled, isCarReady } from "utils/appointmentUtils";
import { getTyreSeasonIcon } from "utils/common";

import type { Appointment, ChecklistTemplate } from "models";
import type { SortedQuestionResult } from "modules/AppointmentDetails/hooks";

type TyreTableItemProps = {
  appointment: Appointment;
  tyreLocation: TYRE_LOCATIONS;
  questionResult: SortedQuestionResult;
  template?: ChecklistTemplate | null;
  readOnly?: boolean;
};

export const TyreTableRow = ({ questionResult, template, appointment, tyreLocation, readOnly }: TyreTableItemProps) => {
  const [showMedia, setShowMedia] = useState(false);

  const { ref: questionRef, isOverflowing: isQuestionOverflowing } = useIsOverflowing<HTMLSpanElement>();
  const { ref: sizeRef, isOverflowing: isSizeOverflowing } = useIsOverflowing<HTMLParagraphElement>();
  const { ref: typeRef, isOverflowing: isTypeOverflowing } = useIsOverflowing<HTMLParagraphElement>();
  const { ref: brandRef, isOverflowing: isBrandOverflowing } = useIsOverflowing<HTMLParagraphElement>();

  const tyrePositionName = useTyrePositionName(questionResult.tyre_position);
  const questionRemarkMutation = useQuestionResultRemarkMutation();
  const appointmentLocation = useAppointmentLocation();

  const canUpdateQuestionResults = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.CarCheckQuestionResults);
  const canUpdateQuestionResultRemark = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.CarCheckQuestionResultRemark);

  const handleRemark = (text: string) => questionRemarkMutation.mutate({ question_result_id: questionResult.id, remark: text });

  const disabledAfterCarReady = !appointmentLocation?.can_edit_checklist_after_car_ready && isCarReady(appointment.appointment_status_identifier);
  const questionResultDisabled = !canUpdateQuestionResults || disabledAfterCarReady || isAppointmentCanceled(appointment.appointment_status_identifier);
  const isRemarkEditDisabled = !canUpdateQuestionResultRemark || questionResultDisabled;
  const tyreSeasonIcon = getTyreSeasonIcon(questionResult.tyre?.season);
  const dbbComment = getDBBComment(
    questionResult.tyre_position,
    tyreLocation === TYRE_LOCATIONS.OnVehicle ? appointment.car?.on_car_tyres : appointment.car?.in_storage_tyres
  );

  return (
    <>
      <GridRow className="TyreTableItem">
        <GridColumn width={2}>
          <div className="tyre-position-container">
            <Popup hoverable disabled={!isQuestionOverflowing} content={tyrePositionName} trigger={<span ref={questionRef}>{tyrePositionName}</span>} />
          </div>
        </GridColumn>
        <GridColumn width={2}>
          <Popup
            hoverable
            disabled={!isSizeOverflowing}
            content={getTyreSize(questionResult.tyre)}
            trigger={
              <div className="tyre-season-container">
                {tyreSeasonIcon && <Icon className={tyreSeasonIcon} />}
                <p ref={sizeRef}>{getTyreSize(questionResult.tyre)}</p>
              </div>
            }
          />
        </GridColumn>
        <GridColumn width={4}>
          <Popup
            hoverable
            disabled={!isTypeOverflowing}
            content={getFullTyreDescription(questionResult.tyre, appointmentLocation?.is_tyre_net_enabled)}
            trigger={<p ref={typeRef}>{getFullTyreDescription(questionResult.tyre, appointmentLocation?.is_tyre_net_enabled)}</p>}
          />
        </GridColumn>
        <GridColumn width={1}>
          <Popup
            hoverable
            disabled={!isBrandOverflowing}
            content={questionResult.tyre?.manufacturer}
            trigger={<p ref={brandRef}>{questionResult.tyre?.manufacturer}</p>}
          />
        </GridColumn>
        <GridColumn width={1}>{questionResult.tyre_profile ? `${questionResult.tyre_profile} mm` : ""}</GridColumn>
        <GridColumn width={3}>
          <Editable hoverable value={questionResult.raw} onSubmit={handleRemark} disabled={readOnly || isRemarkEditDisabled} />
        </GridColumn>
        <GridColumn width={3}>
          <div className="customer-ok-container">
            {dbbComment && (
              <Popup
                content={dbbComment}
                trigger={
                  <div className="dbb-icon-container">
                    <Icon className="tire regular full dbb-comment-icon" />
                  </div>
                }
              />
            )}

            {questionResult.customer_approved && !questionResult.tyre_replacements?.length && (
              <div className="customer-ok-icon-container">
                <Icon className="thumbs up green customer-ok-icon" />
              </div>
            )}

            <TyreActions
              questionResult={questionResult}
              template={template}
              toggleShowMedia={() => setShowMedia(show => !show)}
              appointment={appointment}
              readOnly={readOnly}
            />
          </div>
        </GridColumn>
      </GridRow>

      {showMedia && <QuestionResultMedia questionResult={questionResult} canUpdate={!readOnly && canUpdateQuestionResults} appointment={appointment} />}
    </>
  );
};
